import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { HttpHelperService } from './helper.service';
import { environment } from '../../environment/environment';
import { ApiResponse } from '../models/apiResponse';
import { 
  GraphData, 
  FirstTimeEngagementData, 
  CourseCompletionData,
  QuizCompletionData,
  WeeklyCourseCompletionResult,
  WeeklyEngagementResult,
  CombinedCompletionData, 
  AllCoursesCompletionData,
  QuizPerformanceResponse,
  MAndEData,
  GetMonthlyAveragesResponse,
  MAndELearnerNumberAverage
} from '../models/studentProfileModels';

@Injectable({
  providedIn: 'root'
})
export class ReportsIntergrationService {
  private apiUrl = `${environment.apiBaseUrl}/student-profiles`; 

  constructor(
    private http: HttpClient,
    private httpHelper: HttpHelperService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /**
   * Fetches profile data for a given key and date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso', 'kitsoecd)
   * @param key The profile property key (e.g., 'LwaziAge', 'SatrixGender')
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of GraphData
   */
  getProfileData(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    key: string, 
    startDate: string, 
    endDate: string): Observable<GraphData> {
    const params = new HttpParams()
    .set('start_date', startDate)
    .set('end_date', endDate);

    return this.http.get<ApiResponse<GraphData>>(
      `${this.apiUrl}/${bot}/${key}`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'profile data')),
      catchError(error => this.handleError(error, 'fetching profile data'))
    );
  }

  /**
   * Fetches first-time engagement count for a tenant within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of FirstTimeEngagementData
   */
  getFirstTimeEngagementCount(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    startDate: string, 
    endDate: string
  ): Observable<FirstTimeEngagementData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<FirstTimeEngagementData>>(
      `${this.apiUrl}/${bot}/first-time-engagement`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'first-time engagement count')),
      catchError(error => this.handleError(error, 'fetching first-time engagement count'))
    );
  }

  /**
   * Fetches course completion statistics for a bot and type within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param type The type identifier ('personal', 'business' for lwazi; 'path1' for lesedi; 'path1', 'path2' for kitso)
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of AllCoursesCompletionData
   */
  getCourseCompletionStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<AllCoursesCompletionData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<AllCoursesCompletionData>>(
      `${this.apiUrl}/${bot}/${type}/course-completion-stats`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'course completion stats')),
      catchError(error => this.handleError(error, 'fetching course completion stats'))
    );
  }

  /**
   * Fetches quiz completion statistics for a specific bot and type within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param type The type identifier ('personal', 'business' for lwazi; 'path1' for lesedi; 'path1', 'path2' for kitso)
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of AllCoursesCompletionData
   */
  getQuizCompletionStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<AllCoursesCompletionData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<AllCoursesCompletionData>>(
      `${this.apiUrl}/${bot}/${type}/quiz-completion-stats`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'quiz completion stats')),
      catchError(error => this.handleError(error, 'fetching quiz completion stats'))
    );
  }

  /**
   * Fetches weekly course completion statistics for a bot within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of WeeklyCourseCompletionResult[]
   */
    getWeeklyCourseCompletionStats(
    bot: 'lwazi' | 'lesedi' | 'kitso',
    startDate: string,
    endDate: string
  ): Observable<WeeklyCourseCompletionResult[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<WeeklyCourseCompletionResult[]>>(
      `${this.apiUrl}/${bot}/weekly-course-completion`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, `${bot} weekly course completion stats`)),
      catchError(error => this.handleError(error, `fetching ${bot} weekly course completion stats`))
    );
  }

  /**
   * Fetches weekly first-time engagement statistics for a bot within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of WeeklyEngagementResult[]
   */
  getWeeklyFirstTimeEngagement(
    bot: 'lwazi' | 'lesedi' | 'kitso',
    startDate: string,
    endDate: string
  ): Observable<WeeklyEngagementResult[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<WeeklyEngagementResult[]>>(
      `${this.apiUrl}/${bot}/weekly-first-time-engagement`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, `${bot} weekly first-time engagement`)),
      catchError(error => this.handleError(error, `fetching ${bot} weekly first-time engagement`))
    );
  }

  /**
   * Fetches all-courses completion statistics for a bot and type within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param type The type identifier ('personal', 'business' for lwazi; 'path1' for lesedi; 'path1', 'path2' for kitso)
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of AllCoursesCompletionData
   */
  getAllCoursesCompletionStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<AllCoursesCompletionData> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<AllCoursesCompletionData>>(
      `${this.apiUrl}/${bot}/${type}/all-courses-completion`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'all-courses completion stats')),
      catchError(error => this.handleError(error, 'fetching all-courses completion stats'))
    );
  }
  

  /**
   * Fetches combined course and quiz completion statistics for a bot and type within a date range.
   * @param bot The bot identifier ('lwazi', 'lesedi', 'kitso')
   * @param type The type identifier ('personal', 'business' for lwazi; 'path1' for lesedi; 'path1', 'path2' for kitso)
   * @param startDate Start date in 'YYYY-MM-DD' format
   * @param endDate End date in 'YYYY-MM-DD' format
   * @returns Observable of CombinedCompletionData[]
   */
    getCompletionStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<CombinedCompletionData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<CombinedCompletionData[]>>(
      `${this.apiUrl}/${bot}/${type}/completion-stats`, 
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map((response) => {
        return this.processResponse(response, 'completion stats');
      }),
      catchError(error => this.handleError(error, 'fetching completion stats'))
    );
  }

  getPrePostQuizStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<QuizPerformanceResponse[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<QuizPerformanceResponse[]>>(
      `${this.apiUrl}/${bot}/${type}/pre-post-quiz`,
      { params, headers: this.httpHelper.getHeaders()}
    ).pipe(
      map(response => this.processResponse(response, 'pre-post quiz stats')),
      catchError(error => this.handleError(error, 'fetching pre-post quiz stats'))
    );
  }

  getMAndEStats(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<MAndEData[]> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MAndEData[]>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-quiz`,
      { params, headers: this.httpHelper.getHeaders()}
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e-quiz quiz stats')),
      catchError(error => this.handleError(error, 'fetching m-and-e-quiz quiz stats'))
    );
  }

  getMAndENumbers(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<GetMonthlyAveragesResponse> {
    const params = new HttpParams()
    .set('start_date', startDate) 
    .set('end_date', endDate);

    return this.http.get<ApiResponse<GetMonthlyAveragesResponse>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-numbers`, // ← singular "number"
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e-numbers')),
      catchError(error => this.handleError(error, 'fetching m-and-e learner numbers'))
    );
  }

  getMAndEAverage(
    bot: 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd'| 'kitsozazi'| 'kitsoai' |'mpilo',
    type: 'personal' | 'business' | 'path1' | 'path2',
    startDate: string,
    endDate: string
  ): Observable<MAndELearnerNumberAverage> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get<ApiResponse<MAndELearnerNumberAverage>>(
      `${this.apiUrl}/${bot}/${type}/m-and-e-average`,
      { params, headers: this.httpHelper.getHeaders() }
    ).pipe(
      map(response => this.processResponse(response, 'm-and-e-average')),
      catchError(error => this.handleError(error, 'fetching m-and-e average'))
    );
  }

  /**
   * Processes API response and validates success status
   * @param response The API response
   * @param context The context of the request for error messaging
   * @returns The response data
   * @throws Error if response is invalid
   */
  private processResponse<T>(response: ApiResponse<T>, context: string): T {
    if (!response.success || !response.data) {
      throw new Error(response.error || `Invalid response from server for ${context}`);
    }
    return response.data;
  }

  /**
   * Handles HTTP errors for both browser and server-side contexts.
   * @param error The error to handle
   * @param context The context of the request for error messaging
   * @returns Observable that throws an error with a user-facing message
   */
  private handleError(error: HttpErrorResponse | any, context: string): Observable<never> {
    let errorMessage = `An error occurred while ${context}`;
    if (isPlatformBrowser(this.platformId)) {
      if (error.error instanceof ErrorEvent) {
        errorMessage = `Client/Network Error while ${context}: ${error.error.message}`;
        console.error('Client/Network Error:', error.error.message);
      } else if (error instanceof HttpErrorResponse) {
        const backendError = error.error?.error || error.message;
        errorMessage = `Error ${error.status} while ${context}: ${backendError}`;
        console.error(`Backend error: ${error.status}`, error.error);
      } else {
        errorMessage = `Browser error while ${context}: ${error.message || error}`;
        console.error('Unknown Browser Error:', error);
      }
    } else {
      if (error instanceof HttpErrorResponse) {
        const backendError = error.error?.error || error.message;
        errorMessage = `SSR Error ${error.status} while ${context}: ${backendError}`;
        console.error(`Backend error (Server): ${error.status}`, error.message);
      } else {
        errorMessage = `Server-side error while ${context}: ${error.message || error}`;
        console.error('Server-side Error:', error);
      }
    }
    return throwError(() => new Error(errorMessage));
  }
}